import { ROUTES } from '@shared/constants';
import { ActionType } from '@/constants/action';

// Types
import { FailureReason, FailureConfig } from '@/types/chatbot';
import { SuggestionsType, ToolAvailability } from '@/types/chat';

export const COPILOT_LABEL = {
  TITLE: 'Agility Flash',
  INITIAL_MESSAGE: "Hi, I'm **Agility Flash**, ready to answer your questions!",
  INITIAL_MESSAGE_PREFIX: "Hi, I'm **Agility Flash**",

  getInitialMessageWithRelationship: (relationship: string) =>
    `Hi, I'm **Agility Flash**, ready to answer your questions!\n\nCurrently, you're the **${relationship}** of this user, so you only have permission to give feedback.`,
} as const;

export const CHATBOT_MESSAGES = {
  CANCELLED: `Looks like someone hit the brakes midway. Message didn't make it to the finish line 🚧`,
  INTERRUPTED: 'Action was interrupted, someone tripped over the API cable 🔌',
  HANG_ON: "Agent's probably on a coffee break, try again in a bit ☕",
  ERROR: 'Oops! Something went wrong. Please try again.',
  DEACTIVATE_PERMISSION_DENIED:
    'Unable to deactivate this user because they are an Admin. Please let me know if you need assistance with anything else.',
  ERROR_FALLBACK: 'Oops, a wild bug appeared! Switching to fallback message 🐛',
  ABORTED:
    'Looks like someone changed their mind. Operation was cancelled by the user',
  PROCESSING: `Hang tight, I'm working on it…`,
  SEARCH_SUCCESSFULLY: (action: string) =>
    `Action ${action} was completed successfully. Let me know if there's anything else you need—I'm here to help. I'll also make sure the relevant details are added to memory.`,

  // Employee skills update
  EMPLOYEE_SKILLS_SAVED: 'Employee skills saved successfully!',
  // Project tech stacks update
  PROJECT_TECH_STACKS_SAVED: 'Project technical stacks saved successfully!',
  // Employee education update
  EDUCATION_UPDATE_ERROR:
    'Could not update your education information. Please try again.',
  EDUCATION_UPDATE_PARTIAL_SUCCESS: (successMsg: string, failureMsg: string) =>
    `${successMsg}. However, ${failureMsg}`,
  // Employee certification update
  CERTIFICATION_UPDATE_ERROR:
    'Could not update your certification information. Please try again.',
  CERTIFICATION_UPDATE_PARTIAL_SUCCESS: (
    successMsg: string,
    failureMsg: string,
  ) => `${successMsg}. However, ${failureMsg}`,

  CONFIRMATION_RECEIVED: 'Confirmation received. Processing...',
  EMPLOYEE_DEACTIVATED: 'Employee has been successfully deactivated',
  CONFIRM_MESSAGE: 'Are you sure you want to proceed?',
  CONFIRM_REMOVE_MEMBERS: 'Are you sure to remove these member(s)?',

  PROJECT_EVENTS_SAVED: 'Project events saved successfully!',

  // Delete all
  EMPLOYEE_SKILLS_DELETED_ALL: 'All skills have been removed successfully.',
  EMPLOYEE_EDUCATION_DELETED_ALL:
    'All education records have been removed successfully.',
  EMPLOYEE_CERTS_DELETED_ALL:
    'All certifications have been removed successfully.',

  CONFIRM_ARCHIVE_PROJECT: (projectCode?: string) =>
    projectCode
      ? `Are you sure to archive the project with code ${projectCode}`
      : 'Are you sure to archive this project',
};

export const CHATBOT_SIDEBAR_CLASSES = {
  OPEN: 'sidebar-open',
  CLOSE: 'sidebar-close',
};

export const CHATBOT_EVENTS = {
  SIDEBAR_CLOSED_BY_SEARCH: 'chatbot:sidebar-closed-by-search',
} as const;

export const LONG_RESPONSE_TIME = 120000; // 120s

export const CHATBOT_IMAGE_UPLOAD_EXCLUDED_ROUTES = [
  ROUTES.EMPLOYEES,
  ROUTES.EMPLOYEE_TRAININGS,
];

/**
 * Default configuration for useCopilotAction.
 */
export const DEFAULT_COPILOT_ACTION_CONFIG = {
  AVAILABLE_DISABLED: ToolAvailability.DISABLED, // Tool is only for rendering, agent/UI cannot call it directly
  AVAILABLE_REMOTE: ToolAvailability.REMOTE, // Agent can see and call this Frontend action
  FOLLOW_UP: false, // Action won't trigger follow-up questions from the agent
  RAW_MESSAGE_PARAMETER: {
    name: 'rawMessage',
    type: 'string',
    description: 'Capture entire user message.',
    required: false,
  }, // Default parameter for capturing the entire raw user message
} as const;

/**
 * Default configuration for useHumanInTheLoop.
 */
export const DEFAULT_HUMAN_IN_THE_LOOP_CONFIG = {
  AVAILABLE: ToolAvailability.ENABLED,
} as const;

export enum InvalidItemSection {
  ITEMS_NOT_FOUND = 'itemsNotFound',
  ITEMS_ALREADY_EXISTING = 'itemsAlreadyExisting',
  COLLECTIONS_NOT_FOUND = 'collectionsNotFound',
}

export const ITEM_SECTION_PATTERNS = {
  COLLECTIONS_NOT_FOUND: /(skills?|stacks?) not found to update\/delete/i,
  ITEMS_NOT_FOUND: /(skill|stack) items not found/i,
  ITEMS_ALREADY_EXISTING: /(skill|stack) items are already existing/i,
} as const;

export const FAILURE_PATTERNS = {
  ALREADY_EXISTS: 'already exist',
  NOT_FOUND: 'unmatched',
  NON_EXISTING: 'non-existing',
  EDUCATION_INVALID_DATES: 'invalid graduation or admission dates',
  CERTIFICATION_INVALID_DATES: 'invalid issue date',
  INVALID_URLS: 'invalid credential url',
} as const;

export const LIST_TRUNCATE_LIMIT = 3;

export const FAILURE_CONFIGS: FailureConfig[] = [
  {
    pattern: FAILURE_PATTERNS.ALREADY_EXISTS,
    reason: FailureReason.ALREADY_EXISTS,
    operation: ActionType.ADD,
  },
  {
    pattern: FAILURE_PATTERNS.NOT_FOUND,
    reason: FailureReason.NOT_FOUND,
    operation: ActionType.REMOVE,
  },
  {
    pattern: FAILURE_PATTERNS.INVALID_URLS,
    reason: FailureReason.INVALID_URLS,
    operation: ActionType.ADD,
  },
  {
    pattern: FAILURE_PATTERNS.EDUCATION_INVALID_DATES,
    reason: FailureReason.INVALID_DATES,
    operation: ActionType.ADD,
  },
  {
    pattern: FAILURE_PATTERNS.CERTIFICATION_INVALID_DATES,
    reason: FailureReason.INVALID_DATES,
    operation: ActionType.ADD,
  },
  {
    pattern: FAILURE_PATTERNS.NON_EXISTING,
    reason: FailureReason.NOT_FOUND,
    operation: ActionType.REMOVE,
  },
];

const NOT_FOUND_ERROR_TEMPLATE = {
  format: (s: string, capitalize: boolean) =>
    `${capitalize ? "We couldn't" : "we couldn't"} find ${s} to update/remove`,
} as const;

export const EDUCATION_ERROR_TEMPLATES = {
  [FailureReason.ALREADY_EXISTS]: {
    single: (s: string) => `${s} is already in your profile`,
    multiple: (s: string) => `${s} are already in your profile`,
  },
  [FailureReason.NOT_FOUND]: NOT_FOUND_ERROR_TEMPLATE,
  [FailureReason.INVALID_DATES]: {
    single: (s: string) => `one degree at ${s} has invalid dates`,
    multiple: (s: string) => `${s} have invalid dates`,
  },
  // Education doesn't have credential URLs
  [FailureReason.INVALID_URLS]: {
    single: () => '',
    multiple: () => '',
  },
} as const;

export const CERTIFICATION_ERROR_TEMPLATES = {
  [FailureReason.ALREADY_EXISTS]: {
    single: (s: string) => `that ${s} is already in your certifications`,
    multiple: (s: string) => `that ${s} are already in your certifications`,
  },
  [FailureReason.NOT_FOUND]: NOT_FOUND_ERROR_TEMPLATE,
  [FailureReason.INVALID_DATES]: {
    single: (s: string) => `${s} has invalid issue date`,
    multiple: (s: string) => `${s} have invalid issue dates`,
  },
  [FailureReason.INVALID_URLS]: {
    single: (s: string) => `${s} has invalid credential url`,
    multiple: (s: string) => `${s} have invalid credential url`,
  },
} as const;

// Training card default configuration
export const DEFAULT_CARD_FIELDS = {
  showTopicLabel: true,
  showStatus: true,
  showStart: false,
  showEstEnd: false,
  showActualEnd: false,
  showSupporters: false,
  showCard: true,
} as const;

export const CARD_BACKGROUNDS = {
  DEFAULT:
    'bg-gradient-to-br from-primary-50/60 via-primary-100/40 to-background',
  ARCHIVE:
    'bg-gradient-to-br from-frost-400/40 via-frost-300/30 to-frost-250/30',
} as const;

export const COPILOTKIT_ROUTES = {
  URL: process.env.NEXT_PUBLIC_AGENT_ENDPOINT,
  ROUTE: '/api/copilotkit',
  PROJECT_ROUTE: '/copilotkit/project',
  EMPLOYEE_ROUTE: '/copilotkit/employee',
  SEARCH_ROUTE: '/copilotkit/search',
  PROJECT_AGENT: 'projectAgent',
  EMPLOYEE_AGENT: 'employeeAgent',
  SEARCH_AGENT: 'searchAgent',
};

export const RUNTIME_URL = {
  project: COPILOTKIT_ROUTES.PROJECT_ROUTE,
  employee: COPILOTKIT_ROUTES.EMPLOYEE_ROUTE,
  search: COPILOTKIT_ROUTES.SEARCH_ROUTE,
};

export const CHATBOT_THREAD_ID_KEY = 'space-chatbot-thread-id';

export const SUGGESTION_CONFIG = {
  LOADING_DELAY: 600, // Delay between each chip reveal cycle (ms)
  CHIP_STAGGER_MS: 60, // Stagger offset per chip entrance animation (ms)
  TRIGGER_LABEL: '💡 I can help with...', // Label when panel is closed
  TRIGGER_LABEL_OPEN: 'Hide suggestions', // Label when panel is open
} as const;

export const USER_TESTING_SUGGESTIONS =
  process.env.NEXT_PUBLIC_USER_TESTING_SUGGESTIONS;

export const SUGGESTION_UNARCHIVE_PROJECT: SuggestionsType = {
  id: 'unarchive-project',
  label: 'Unarchive project',
  prompt: `I'd like to unarchive this project`,
};

export const SUGGESTIONS_PROJECT_PAGE: SuggestionsType[] = [
  {
    id: 'update-project-info',
    label: 'Update project info',
    prompt: `I'd like to update the project info`,
  },
  {
    id: 'invite-members',
    label: 'Invite members',
    prompt: `I'd like to invite new members to this project`,
  },
  {
    id: 'update-technical-stacks',
    label: 'Update technical stacks',
    prompt: `I'd like to update the technical stacks for this project`,
  },
  {
    id: 'add-event',
    label: 'Add event',
    prompt: `I'd like to add a new event to this project`,
  },
];

export const SUGGESTIONS_EMPLOYEES_ADMIN = (employee: string) => {
  return [
    {
      id: 'assign-mentor',
      label: 'Assign mentor',
      prompt: `I'd like to assign mentor for this employee`,
    },
    {
      id: 'assign-mentee',
      label: 'Assign mentee',
      prompt: `I'd like to assign mentee for this employee`,
    },
    {
      id: 'invite-members',
      label: `Invite ${employee} to join project`,
      prompt: `I'd like to invite this employee to project`,
    },
    {
      id: 'leave-members',
      label: `Leave ${employee} from project`,
      prompt: `I'd like to leave this employee from project`,
    },
  ];
};

export const SUGGESTION_REACTIVE_EMPLOYEE: SuggestionsType = {
  id: 'reactivate-employee',
  label: 'Reactivate employee',
  prompt: `I'd like to reactivate this employee`,
};

const CHIP_CREATE_TOPIC_BASE = {
  id: 'create-topic',
  label: 'Create topic',
} as const;

const CHIP_CREATE_TOPIC_FOR_ADMIN_ROLE: SuggestionsType = {
  ...CHIP_CREATE_TOPIC_BASE,
  prompt: `I'd like to create new topic for this employee`,
};

const CHIP_CREATE_TOPIC_FOR_USER_ROLE: SuggestionsType = {
  ...CHIP_CREATE_TOPIC_BASE,
  prompt: `I'd like to create new topic`,
};

const CHIP_ASSIGN_SUPPORTER: SuggestionsType = {
  id: 'assign-supporters',
  label: 'Assign supporters',
  prompt: `I'd like to assign supporter for this employee`,
};

const CHIP_UNASSIGN_SUPPORTER: SuggestionsType = {
  id: 'unassign-supporters',
  label: 'Unassign supporters',
  prompt: `I'd like to unassign supporter of this employee`,
};

export const SUGGESTIONS_EMPLOYEES_MENTOR = (
  isAdmin: boolean,
): SuggestionsType[] => [
  isAdmin ? CHIP_CREATE_TOPIC_FOR_ADMIN_ROLE : CHIP_CREATE_TOPIC_FOR_USER_ROLE,
  CHIP_ASSIGN_SUPPORTER,
  CHIP_UNASSIGN_SUPPORTER,
];

export const SUGGESTIONS_EMPLOYEES_USER: SuggestionsType[] = [
  CHIP_CREATE_TOPIC_FOR_USER_ROLE,
  {
    id: 'update-skills',
    label: 'Update skills',
    prompt: `I'd like to update my skills`,
  },
  {
    id: 'update-information',
    label: 'Update your information',
    prompt: `I'd like to update my information`,
  },
];

export const SUGGESTIONS_TRAINING_ADMIN: SuggestionsType[] = [
  {
    id: 'assign-mentor',
    label: 'Assign mentor',
    prompt: `I'd like to assign mentor for this employee`,
  },
  CHIP_CREATE_TOPIC_FOR_ADMIN_ROLE,
];

export const SUGGESTIONS_TRAINING_MENTOR = (
  isAdmin: boolean,
): SuggestionsType[] => [
  isAdmin ? CHIP_CREATE_TOPIC_FOR_ADMIN_ROLE : CHIP_CREATE_TOPIC_FOR_USER_ROLE,
  CHIP_ASSIGN_SUPPORTER,
  CHIP_UNASSIGN_SUPPORTER,
  {
    id: 'request-feedback',
    label: 'Request feedback',
    prompt: `I'd like to request feedback for this trainee`,
  },
];

const CHIP_CHANGE_TOPIC_STATUS: SuggestionsType = {
  id: 'change-topic-status',
  label: 'Change topic status',
  prompt: `I'd like to update topic status`,
};

export const SUGGESTIONS_TRAINING_SUPPORTER: SuggestionsType[] = [
  {
    id: 'provide-feedback',
    label: 'Provide feedback',
    prompt: `I'd like to provide feedback for trainee`,
  },
  CHIP_CHANGE_TOPIC_STATUS,
];

export const SUGGESTIONS_TRAINING_USER: SuggestionsType[] = [
  CHIP_CHANGE_TOPIC_STATUS,
  CHIP_CREATE_TOPIC_FOR_USER_ROLE,
  {
    id: 'create-presentation',
    label: 'Create presentation',
    prompt: `I'd like to create presentation`,
  },
];

export const CHAT_PAGE_CONTEXT = {
  EMPLOYEE: 'employee',
  TRAINING: 'training',
  PROJECT: 'project',
} as const;
